import { TimePreset } from '../../../../schema/hannibal/types.generated';
import { FilterChoice } from '../../types/FilterChoice';
import { PathMaker } from '../../types/PathParser';
import { makeDateSegment, parseDate } from './dateSegment';
import { makeLocationSegment, parseMetro, parseState, parseSuburb } from './locationSegment';
import { makeTopicSegment, parseTopic } from './topicSegment';

export const makeWholePath: PathMaker = function* (filters) {
  /** All search URLs begin with either 'appointments' or 'find' */
  yield filters.onlineAppointments ? 'appointments' : 'find';
  /** URLs optionally contain a token for the consultation type */
  if (filters.flag) yield filters.flag;
  /** After-hours searches get a special token in their URL; any other time-search gets represented as a query param */
  if (filters.onlineAppointments?.time === TimePreset.AfterHours) yield 'after-hours';
  yield* makeTopicSegment(filters);
  yield* makeLocationSegment(filters);
  yield* makeDateSegment(filters);
};

export const parseWholePath = (tokens: string[]) => {
  const filters: FilterChoice = {};
  let treatmentExpected = false;
  const previousToken = [];

  for (const rawToken of tokens) {
    const token = rawToken.toLowerCase();

    switch (token) {
      case 'search': /** The 'search' prefix is vestigial, so we ignore it */
      case 'find': /** 'find' is a no-op, since we default to including results without appointments */
      case 'australia': /** Australia is a no-op, present when there is no chosen location. */
      case 'near-me': /** Near-Me is a no-op, recognised for backwards compatibility. */
      case 'text' /** 'text' is a no-op indicating that there is no topic */:
        continue;

      case 'appointments':
        filters.onlineAppointments = { ...filters.onlineAppointments };
        previousToken.push(token);
        continue;
      case 'telehealth':
        filters.flag = 'telehealth';
        continue;
      case 'after-hours':
        filters.onlineAppointments = { time: TimePreset.AfterHours };
        continue;

      case 'next7days':
        filters.onlineAppointments = { ...filters.onlineAppointments, date: 'Next7Days' };
        continue;
      case 'today':
        filters.onlineAppointments = { ...filters.onlineAppointments, date: 'Today' };
        continue;
      case 'tomorrow':
        filters.onlineAppointments = { ...filters.onlineAppointments, date: 'Tomorrow' };
        continue;
      case 'nextavailable':
        filters.onlineAppointments = { ...filters.onlineAppointments };
        continue;
      /** The 'availability' token is a bit of a mystery — it appears on inbound links and in Google results.
       * We interpret it to mean a topicless search for online-bookable appointments.
       */
      case 'availability':
        filters.onlineAppointments = { ...filters.onlineAppointments };
        continue;
      case 'bulk-billing-gp':
        filters.topic = { bulkBillingGp: true, specialist: 'GP' };
        continue;

      /** The presence of a 'treatment separator' indicates that the next token should be taken as a
       * treatment rather than a specialist. We only emit the term 'treatment' as a separator, but we
       * recognise multiple separators for backwards-compatibility.
       */
      case 'treatment':
      case 'service':
      case 'interest':
        treatmentExpected = true;
        continue;
    }

    if (parseState(token, filters)) continue;
    if (parseMetro(token, filters)) continue;
    if (parseSuburb(token, filters)) continue;
    if (parseDate(token, filters)) continue;

    /** If the token doesn't match any other patterns, it must be topic. */
    parseTopic(previousToken, token, filters, treatmentExpected);
  }

  return filters;
};
